import './index.scss';
import moment from 'moment';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { Clock, MapPin } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import {
  GymResource,
  saveGymResource,
} from '../../../../store/gym-management/GymTypes';
import { getSingleOption } from '../../../../utility/Utils';
import BookGymDateInformation from '../RoomGymDateIformaton';
import {
  useCloseOldModal,
  useOpenOldModal,
} from '../../../../store/modal/modalHooks';
import { useUpdateBookGymRoom } from '../../../../store/gym-management/GymHooks';
import { formatNumberToDayName } from '../../../../utility/services/get-day-names';
import BookingCheckout, {
  CreditTypesEnum,
} from '../../../shared/BookingCheckout';
import {
  useCancelBookResource,
  useFetchResourceAlloc,
  useResourceManagementSlots,
} from '../../../../store/RoomManagement/resourceManagementHooks';
import { ModalComponentEnum } from '../../../../store/modal/modalTypes';
import Toast from '../../../shared/Toast/Toast';
import { MyActivityResourceImpl } from '../../../../API/my-activities/implements/my-activity-resource.impl';

type PropsTypes = {
  activityId: number;
  form: UseFormReturn<any, object>;
  gym: GymResource;
  setShowSuccessPage: (value: boolean) => void;
  setShowCancellationPolicy: (value: boolean) => void;
  showCancellationPolicy: boolean;
  activity: MyActivityResourceImpl;
};

const ModifyGymForm = (props: PropsTypes) => {
  const { t } = useTranslation();
  const closeModal = useCloseOldModal();
  const updateBookGymRoom = useUpdateBookGymRoom();
  const resourceSlots = useResourceManagementSlots();
  const fetchResourceAlloc = useFetchResourceAlloc();
  const openModal = useOpenOldModal();
  const cancelResource = useCancelBookResource();

  const [totalPrice, setTotalPrice] = useState(0);

  const onCancelBooking = () => {
    openModal(ModalComponentEnum.QUESTION_CANCEL_ALLOC_MODAL, {
      data: { activity: props.activity },
      callBack: () => {
        cancelResource(props.activityId, (isSuccessful, message) => {
          const toastType = isSuccessful ? 'success-request' : 'error';
          Toast(toastType, message, t);
        }).finally(() => {
          closeModal();
        });
      },
    });
  };

  const onSubmit = async (data: any) => {
    await onBookGymRoom(data);
  };

  useEffect(() => {
    if (props.form.watch('date')) {
      const timeStamp = new Date(props.form.watch('date')).getTime();
      fetchResourceAlloc(props.gym.id, timeStamp, props.activityId).finally();
    }
  }, [props.form.watch('date').toString()]);

  const onBookGymRoom = async (data: any) => {
    const newDate: saveGymResource = {
      resourceId: props.gym.id!,
      date: moment(data.date[0]).format('YYYY-MM-DD'),
      startTime: +data.start,
      endTime: +data.end,
      numberOfParticipants: 1,
      allocId: props.activityId,
    };

    const { isSuccessfull, errorMessage } = await updateBookGymRoom(newDate);
    if (isSuccessfull) {
      onSuccess();
    } else {
      onFailure(errorMessage);
    }
  };

  const onSuccess = () => {
    props.setShowSuccessPage(true);
  };

  const onFailure = (errorMessage: string) => {
    toast.error(errorMessage, {
      icon: false,
      hideProgressBar: true,
      autoClose: 5000,
    });
  };

  useEffect(() => {
    if (
      !props.form.watch('start') ||
      !props.form.watch('end') ||
      !props?.gym?.bookingPrice
    ) {
      setTotalPrice(0);
      return;
    }
    setTotalPrice(
      (props.form.watch('end') - props.form.watch('start')) *
        props?.gym?.bookingPrice
    );
  }, [props.form.watch('start'), props.form.watch('end')]);

  const onShowPolicy = () => {
    if (props.showCancellationPolicy) {
      props.setShowCancellationPolicy(false);
    } else {
      props.setShowCancellationPolicy(true);
    }
  };

  return (
    <form onSubmit={props.form.handleSubmit(onSubmit)}>
      <div className="book-gym-form">
        <div className="book-gym-form-content">
          <div className="book-gym-form-title">
            {t('GYM_ROOM_DETAILS_TITLE')}
          </div>
          <div className="book-gym-form-details">
            <div className="book-gym-form-detail-item">
              <div className="book-gym-form-detail-item-icon">
                <Clock size={18} />
              </div>
              <div className="book-gym-form-detail-item-location">
                <div className="book-gym-form-detail-item-location-name">
                  {getSingleOption(props.gym.availableFrom).label} -{' '}
                  {getSingleOption(props.gym.availableTo).label}
                </div>
                <div className="book-gym-form-detail-item-location-building">
                  {props.gym.availableDays.length === 7 && 'Whole seek'}
                  {props.gym.availableDays.length !== 7 &&
                    props.gym.availableDays
                      .sort((a, b) => a - b)
                      .map((day) => formatNumberToDayName(day))
                      .join(', ')}
                </div>
              </div>
            </div>
            <div className="book-gym-form-detail-item">
              <div className="book-gym-form-detail-item-icon">
                <MapPin size={18} />
              </div>
              <div className="book-gym-form-detail-item-location">
                <div className="book-gym-form-detail-item-location-name">
                  {props.gym.location?.name}
                </div>
                <div className="book-gym-form-detail-item-location-building">
                  {props.gym.building?.name}
                </div>
              </div>
            </div>
          </div>
          {props.gym && (
            <BookGymDateInformation
              gymRoom={props.gym!}
              form={props.form}
              originalArray={resourceSlots}
            />
          )}
          <div className="book-gym-prices">
            <BookingCheckout
              creditTypes={CreditTypesEnum.COMPANY}
              price={totalPrice}
              disabledCreditUser={!props.gym?.enableCreditUse}
            />
          </div>
        </div>
        <div className="book-gym-form-actions">
          <Button
            className="confirm-booking-gym-button"
            type="submit"
            color="primary"
            disabled={!props.form.watch('start') || !props.form.watch('end')}
          >
            {t('BOOK_GYM_TIME_BUTTON')}
          </Button>
          <Button
            className="cancel-booking-gym-button"
            type="button"
            onClick={onCancelBooking}
            color="transparent"
            style={{ backgroundColor: '#ea5455', color: 'white' }}
          >
            {t('CANCEL_BOOKING')}
          </Button>
          <Button
            className="cancel-booking-gym-button"
            type="button"
            color="transparent"
            onClick={closeModal}
          >
            {t('CLOSE_BUTTON')}
          </Button>
        </div>
        <div className="view-cancellation-policy-btn">
          <span onClick={() => onShowPolicy()}>
            {props.showCancellationPolicy
              ? t('BACK_BUTTON_TEXT')
              : t('VIEW_CANCELLATION_POLICY')}
          </span>
        </div>
      </div>
    </form>
  );
};

export default ModifyGymForm;
